import React from "react";
import {
  CAPA_STEPPER_STEPS,
  getStageIndex,
  capaDetailsAreComplete,
} from "./capaWorkflow";

/**
 * @param {{
 *   detail: Record<string, unknown> | null;
 *   currentStatus: string;
 *   selectedStepIndex: number;
 *   onStepClick?: (stepIndex: number) => void;
 * }} props
 */
const CapaStageStepper = ({
  detail,
  currentStatus,
  selectedStepIndex,
  onStepClick,
}) => {
  const workflowIndex = getStageIndex(currentStatus);
  const isClosed = currentStatus === "Closed";
  const detailsComplete = capaDetailsAreComplete(detail);
  const verificationIdx = getStageIndex("Verification");
  const closureIdx = getStageIndex("Closure");

  /** Which step is “current” (blue): when Open + details done, focus moves to Investigation. */
  const activeIndex =
    currentStatus === "Open" && !detailsComplete
      ? 0
      : currentStatus === "Open" && detailsComplete
        ? 1
        : workflowIndex;

  const isStepComplete = (i) => {
    if (isClosed) return true;
    if (i < workflowIndex) return true;
    if (i === 0 && detailsComplete && workflowIndex === 0) return true;
    return false;
  };

  const isStepActive = (i) => {
    if (isClosed) return false;
    if (isStepComplete(i)) return false;
    return i === activeIndex;
  };

  const isConnectorDone = (i) => {
    if (i >= CAPA_STEPPER_STEPS.length - 1) return false;
    if (isClosed) return true;
    if (i < workflowIndex) return true;
    if (i === 0 && detailsComplete && workflowIndex === 0) return true;
    return false;
  };

  const isStepClickable = (i) => {
    if (isClosed) return true;
    if (workflowIndex === 0 && i > 0) return false;
    if (i === 0) return true;
    if (i <= workflowIndex) return true;
    if (workflowIndex === verificationIdx && i === closureIdx) return true;
    return false;
  };

  return (
    <div className="capa-stage-stepper-outer">
      <nav className="capa-stage-stepper" aria-label="CAPA workflow stages">
      {CAPA_STEPPER_STEPS.map((step, i) => {
        const complete = isStepComplete(i);
        const active = isStepActive(i);
        const clickable = isStepClickable(i);
        const selected = selectedStepIndex === i;
        const iconClass = step.icon ? `fa-${step.icon}` : "fa-circle";

        let nodeClass = "capa-stage-stepper__node capa-stage-stepper__node--upcoming";
        if (complete) {
          nodeClass = "capa-stage-stepper__node capa-stage-stepper__node--complete";
        } else if (active) {
          nodeClass = "capa-stage-stepper__node capa-stage-stepper__node--active";
        }
        if (selected) {
          nodeClass += " capa-stage-stepper__node--selected";
        }

        let labelClass = "capa-stage-stepper__label--upcoming";
        if (selected) {
          labelClass = complete
            ? "capa-stage-stepper__label--selected capa-stage-stepper__label--selected-complete"
            : "capa-stage-stepper__label--selected";
        } else if (active) {
          labelClass = "capa-stage-stepper__label--active";
        } else if (complete) {
          labelClass = "capa-stage-stepper__label--complete";
        }

        return (
          <button
            key={step.status}
            type="button"
            className={`capa-stage-stepper__segment ${
              isConnectorDone(i) ? "capa-stage-stepper__segment--connector-done" : ""
            }`}
            disabled={!clickable}
            title={
              !clickable
                ? "Complete previous steps first"
                : `${step.label} (${step.status})`
            }
            onClick={() => clickable && onStepClick?.(i)}
          >
            <div className="capa-stage-stepper__node-wrap">
              <div className={nodeClass}>
                {complete ? (
                  <i className="fas fa-check" aria-hidden />
                ) : (
                  <i className={`fas ${iconClass}`} aria-hidden />
                )}
              </div>
            </div>
            <div className={`capa-stage-stepper__label ${labelClass}`}>
              {step.label}
            </div>
          </button>
        );
      })}
      </nav>
    </div>
  );
};

export default CapaStageStepper;
